/**
 * VFS mount table — the pure half of the service worker's `webdav-mount`
 * plugin (sw-middleware-plugins, Phase 2).
 *
 * A mount binds a path in this origin's namespace (or, cooperatively, in
 * another origin's) to a WebDAV server. Records live in the `acequiaMounts`
 * IndexedDB store, keyed by the value clients have always used; two shapes
 * are accepted:
 *
 *   legacy   { path: '/events', serverUrl, subPath }
 *            → mountpoint `/acq/events` (the /acq alias is permanent)
 *   current  { mountpoint: '/files', domain?, serverUrl, subPath?, credentialsKey? }
 *
 * Credentials are keyed **per binding** — `credentialsKey`, defaulting to
 * `serverUrl` so legacy records keep resolving the credential they always
 * did. A new mount that wants its own short code sets `credentialsKey`
 * (one anchor, one leaf, one code per mount).
 *
 * `domain` is the binding's domain in swRegistry terms: `'self'` (default)
 * or an anchored origin — a mount on another origin is an explicit
 * cross-origin responsibility that lifts the SW's default pass-through
 * for exactly that (origin, path).
 *
 * Who may write a record is not checked — any same-origin page can — so
 * the worker decides what a record may bind
 * (service-worker-runtime-plugin-registration, 2026-10-29). The mount
 * plugin runs ahead of the content-addressed carve-out and forwards the
 * page's headers and body, so a mount at `/` or `/auth` stood in front of
 * the auth API and every page, and a local mount there needed no server
 * and outlived the page. The rules:
 *   - **Platform paths** (`/auth`, `/groups`) are never a mount's, on any
 *     origin, and a mount above them leaves them alone (`except` on its
 *     binding): the server's auth API and the group routes always answer.
 *   - **App paths** (`/`, `/src`, `/build`, `/web_modules`, `/node_modules`)
 *     on this origin take a grant: the mountpoint listed in
 *     `config.swPlugins['webdav-mount'].mountpoints`. A granted `/` is a
 *     domain served from a mount (hosting-a-domain-from-a-browser-tab), with
 *     `/auth` delegated to the server; it still leaves the other app paths
 *     alone — serving one takes its own granted mount there.
 *   - **Another origin** takes a grant: the origin listed in
 *     `config.swPlugins['webdav-mount'].origins`. Claiming an origin is the
 *     deployer's grant, never the registering page's choice.
 * A grant is config, so a deployer's mistake can't lock a browser out: a
 * page opened with `?acequia-mounts=off` (and what it loads) bypasses every
 * mount — the way back to the Files app to remove one (sw.js).
 */

import { normalizeBinding, normalizePath, pathMatches, bindingMatches, SELF } from './swRegistry.js'

export const ACQ_ALIAS = '/acq'

/**
 * Does this page URL carry the way back, `?acequia-mounts=off`? On its own
 * query, or inside its `returnTo`: a server redirects an anonymous page to
 * its login page with the original URL there, and the flag was lost at that
 * redirect — a signed-out visitor to a root-mounted origin could not get
 * back (seen in production, 2026-10-29). One level, same origin only.
 */
export function mountsOffIn(url) {
    let u
    try {
        u = new URL(url)
    } catch {
        return false
    }
    const off = (params) => params.get('acequia-mounts') === 'off'
    if (off(u.searchParams)) return true
    const returnTo = u.searchParams.get('returnTo')
    if (!returnTo) return false
    try {
        const back = new URL(returnTo, u.origin)
        return back.origin === u.origin && off(back.searchParams)
    } catch {
        return false
    }
}

/** Never a mount's, on any origin, granted or not; a mount above one leaves it alone. */
export const PLATFORM_PATHS = Object.freeze(['/auth', '/groups'])

/** On this origin, a mount at or under one of these needs a granted mountpoint. */
export const APP_PATHS = Object.freeze(['/', '/src', '/build', '/web_modules', '/node_modules'])

/**
 * Every reserved mountpoint: what the datasources API refuses for its own
 * writes (it takes no grants), and what the worker refuses without one.
 */
export const RESERVED_MOUNTPOINTS = Object.freeze([...APP_PATHS, ...PLATFORM_PATHS])

const atOrUnder = (prefix, path) => path === prefix || (prefix !== '/' && pathMatches(prefix, path))

/** The reserved prefix a (normalized) mountpoint falls on, or null. */
export function reservedMountpoint(mountpoint) {
    return RESERVED_MOUNTPOINTS.find((r) => atOrUnder(r, mountpoint)) ?? null
}

/** Reserved paths strictly below a mountpoint, which its binding leaves alone. */
function exceptFor(mountpoint, domain) {
    const below = (r) => r !== mountpoint && r !== '/' && pathMatches(mountpoint, r)
    const list = domain === SELF ? RESERVED_MOUNTPOINTS : PLATFORM_PATHS
    return list.filter(below)
}

/**
 * The mount grants in config, `config.swPlugins['webdav-mount']`:
 * `{ origins: [origin, …], mountpoints: [path, …] }`. Returns the lists
 * normalizeMount takes and a warning per thing ignored, so a typo is said
 * rather than silently granting or refusing.
 */
export function mountGrants(pluginConfig) {
    const warnings = []
    const allowedOrigins = []
    const allowedMountpoints = []
    const list = (field) => {
        const v = pluginConfig?.[field]
        if (v === undefined) return []
        if (Array.isArray(v)) return v
        warnings.push(`config.swPlugins['webdav-mount'].${field} must be an array — ignored`)
        return []
    }
    for (const o of list('origins')) {
        try {
            const d = normalizeBinding({ domain: o }).domain
            if (d === SELF || d === '*') throw new Error()
            allowedOrigins.push(d)
        } catch {
            warnings.push(`config.swPlugins['webdav-mount'].origins: ${JSON.stringify(o)} is not a bare origin — ignored`)
        }
    }
    for (const p of list('mountpoints')) {
        const mp = normalizePath(p)
        const platform = PLATFORM_PATHS.find((r) => atOrUnder(r, mp))
        if (platform) warnings.push(`config.swPlugins['webdav-mount'].mountpoints: ${mp} can't be granted — ${platform} is the platform's`)
        else allowedMountpoints.push(mp)
    }
    return { allowedOrigins, allowedMountpoints, warnings }
}

/**
 * Normalize one stored record into the shape the plugin works with, or null.
 * `allowedOrigins` and `allowedMountpoints` are the grants (mountGrants);
 * `onRefused(key, reason)` hears about a well-formed record refused for
 * what it binds (a malformed one is just null, as before).
 */
export function normalizeMount(key, record, { allowedOrigins = [], allowedMountpoints = [], onRefused = null } = {}) {
    const refuse = (reason) => {
        onRefused?.(String(key), reason)
        return null
    }
    if (!record || typeof record !== 'object') return null
    // A LOCAL mount (a-local-opfs-mount-type-in-the-service-worker): its
    // source is a directory in this origin's OPFS, served by the bundled
    // BrowserDAV engine inside the worker — no server, no credential, no
    // network. `{ mountpoint, source: 'opfs', path: '<dir>' }`.
    const local = record.source === 'opfs' || typeof record.opfs === 'string'
    if (!local && (typeof record.serverUrl !== 'string' || !record.serverUrl)) return null
    let mountpoint
    if (typeof record.mountpoint === 'string' && record.mountpoint) {
        mountpoint = normalizePath(record.mountpoint)
    } else {
        const rel = typeof record.path === 'string' ? record.path : key
        mountpoint = normalizePath(ACQ_ALIAS + normalizePath(rel))
    }
    let domain
    try {
        domain = normalizeBinding({ domain: record.domain }).domain
    } catch {
        return null
    }
    if (domain === '*') return null // a mount is a place, not a wildcard
    const platform = PLATFORM_PATHS.find((r) => atOrUnder(r, mountpoint))
    if (platform) return refuse(`a mount may not take ${mountpoint} — ${platform} is the platform's, on every origin`)
    if (domain === SELF) {
        const app = APP_PATHS.find((r) => atOrUnder(r, mountpoint))
        if (app && !allowedMountpoints.includes(mountpoint)) {
            return refuse(`a mount at ${mountpoint} needs that mountpoint in config.swPlugins['webdav-mount'].mountpoints${app === '/' ? ' — it would stand in front of every page on this origin' : ''}`)
        }
    } else if (!local && !allowedOrigins.includes(domain)) {
        return refuse(`a mount on ${domain} needs that origin in config.swPlugins['webdav-mount'].origins`)
    }
    const except = Object.freeze(exceptFor(mountpoint, domain))
    if (local) {
        if (domain !== SELF) return null // OPFS is this origin's storage
        const raw = typeof record.opfs === 'string' ? record.opfs : record.path
        const opfsPath = normalizePath(raw || key).replace(/^\/+/, '')
        if (!opfsPath) return null // never the OPFS root itself
        return Object.freeze({
            id: String(key),
            domain,
            mountpoint,
            except,
            source: 'opfs',
            opfsPath,
            serverUrl: null,
            subPath: '',
            credentialsKey: null,
        })
    }
    const subPath = record.subPath ? normalizePath(record.subPath) : ''
    return Object.freeze({
        id: String(key),
        domain,
        mountpoint,
        except,
        source: 'remote',
        serverUrl: record.serverUrl.replace(/\/+$/, ''),
        subPath: subPath === '/' ? '' : subPath,
        credentialsKey: record.credentialsKey || record.serverUrl,
    })
}

export function isLocalMount(mount) {
    return mount?.source === 'opfs'
}

/**
 * The engine request for a local mount — the shape `WebDAVFileSystem.
 * handleRequest` takes (the conformance adapter's), built from a fetch
 * Request. `path` is the client-visible pathname (so the engine derives the
 * route prefix and emits hrefs under the mountpoint); `remainingPath` is
 * the path inside the mount. Bodies: `rawBody` (ArrayBuffer) for PUT,
 * text `body` for the XML methods. `crossServer.selfHosts` names this
 * origin's host (the engine compares `url.host`) so an absolute same-origin
 * Destination on MOVE/COPY is local, not a cross-server push.
 */
export function localRequestFor(mount, url, { method, headers, rawBody = null, body = null, bodyStream = null, requestId, origin }) {
    const pathname = url.pathname
    let remainingPath = pathname.startsWith(mount.mountpoint) ? pathname.slice(mount.mountpoint.length) : pathname
    if (!remainingPath.startsWith('/')) remainingPath = '/' + remainingPath
    const h = {}
    const entries = typeof headers?.entries === 'function' ? headers.entries() : Object.entries(headers || {})
    for (const [k, v] of entries) h[String(k).toLowerCase()] = v
    const query = {}
    for (const [k, v] of url.searchParams) query[k] = v
    return {
        method,
        path: pathname,
        remainingPath,
        query,
        requestId: requestId || `local-${Date.now().toString(36)}`,
        headers: h,
        body,
        rawBody,
        ...(bodyStream ? { bodyStream } : {}),
        crossServer: { selfHosts: [new URL(origin).host] },
    }
}

/** Normalize a whole store dump ({ key: record }) into a table. Options as normalizeMount's. */
export function normalizeMountTable(entries, options = {}) {
    const out = []
    for (const [key, record] of Object.entries(entries || {})) {
        const m = normalizeMount(key, record, options)
        if (m) out.push(m)
    }
    return out
}

/**
 * Bindings the mount plugin (and the htmx wrapper) present to the
 * registry: one per distinct (domain, mountpoint), plus the permanent
 * `/acq` alias on self so an unmounted `/acq/...` still answers
 * "Mount not found" instead of falling through.
 */
export function bindingsForMounts(mounts) {
    const seen = new Set()
    const out = []
    const add = (domain, path, except = []) => {
        const k = domain + ' ' + path
        if (seen.has(k)) return
        seen.add(k)
        out.push(except.length ? { domain, path, except: [...except] } : { domain, path })
    }
    add(SELF, ACQ_ALIAS)
    for (const m of mounts) add(m.domain, m.mountpoint, m.except || [])
    return out
}

/** A mount as a binding: its place, less the reserved paths below it. */
function mountBinding(m) {
    return { domain: m.domain, path: m.mountpoint, except: m.except }
}

/** Longest-mountpoint match among mounts whose binding matches this URL. */
export function matchMount(mounts, url, origin) {
    let best = null
    for (const m of mounts) {
        if (!bindingMatches(mountBinding(m), url, origin)) continue
        if (!best || m.mountpoint.length > best.mountpoint.length) best = m
    }
    return best
}

/** Is this URL under the /acq alias on the SW's own origin? */
export function underAcqAlias(url, origin) {
    return url.origin === origin && pathMatches(ACQ_ALIAS, url.pathname)
}

/**
 * The path inside a mount, always starting with `/`. Slicing the mountpoint
 * off left a root mount's paths without their slash (`/b.txt` → `b.txt`).
 */
function restOfPath(mount, pathname) {
    if (pathname === mount.mountpoint) return '/'
    return mount.mountpoint === '/' ? pathname : pathname.slice(mount.mountpoint.length)
}

/**
 * Translate a VFS request path into the upstream-relative path the WebDAV
 * client is given, and the two prefixes used to rewrite bodies (upstream
 * hrefs → VFS hrefs) and Destination headers (VFS → upstream).
 */
export function resolveMountPath(mount, pathname) {
    const rest = restOfPath(mount, pathname)
    return {
        relativePath: (mount.subPath || '') + rest,
        vfsPrefix: mount.mountpoint,
        serverPrefix: mount.serverUrl + (mount.subPath || ''),
    }
}

/**
 * Rewrite a VFS Destination header (MOVE/COPY) to the upstream URL. A
 * destination outside this mount is refused (null) — cross-mount moves
 * are not a single WebDAV operation.
 */
export function rewriteDestination(mount, destination, origin) {
    if (!destination) return null
    let url
    try {
        url = new URL(destination, origin)
    } catch {
        return null
    }
    if (!bindingMatches(mountBinding(mount), url, origin)) return null
    const { relativePath, serverPrefix } = resolveMountPath(mount, url.pathname)
    void relativePath
    return serverPrefix + restOfPath(mount, url.pathname)
}
